import React from "react";

import { useInternalFeatures } from "~/hooks/use-internal-features";

import { useMeditationCacheStatus } from "../hooks/use-meditation-cache-status";
import type { MeditationPauseMultiplier, MeditationType } from "../types";

interface Props {
    type: MeditationType;
    voice: string;
    pauseMultiplier?: MeditationPauseMultiplier;
    sessionId?: string;
    partInteractionId?: string;
}

// AIDEV-NOTE: Internal-only "cached / not cached" badge shown next to Play
// controls. Gated behind the Internal Features flag so end users (and
// practitioners without the flag) never see it. Lets internal users know
// whether the next Play click will be instant (cached MP3) or has to generate
// via ElevenLabs first (slower, costs credits).
export const MeditationCacheBadge: React.FC<Props> = ({
    type,
    voice,
    pauseMultiplier = 1,
    sessionId,
    partInteractionId,
}) => {
    const { enabled: internalFeaturesEnabled } = useInternalFeatures();
    const show = internalFeaturesEnabled;

    const { data, isLoading, isError } = useMeditationCacheStatus({
        type,
        voice,
        pauseMultiplier,
        sessionId,
        partInteractionId,
        enabled: show,
    });

    if (!show) return null;

    let label: string;
    let className: string;
    if (isLoading) {
        label = "checking…";
        className = "bg-gray-100 text-gray-500";
    } else if (isError) {
        label = "status?";
        className = "bg-yellow-100 text-yellow-700";
    } else if (data?.cached) {
        label = "cached";
        className = "bg-green-100 text-green-700";
    } else {
        label = "not cached";
        className = "bg-amber-100 text-amber-700";
    }

    return (
        <span
            data-testid="meditation-cache-badge"
            className={`inline-flex items-center rounded-full px-2 py-0.5 font-mono text-[10px] uppercase tracking-wide ${className}`}
            title={
                data?.storagePath
                    ? `Expected path: ${data.storagePath}`
                    : undefined
            }
        >
            {label}
        </span>
    );
};
